import { useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { useProposalsApi } from "../api";
import { ProposalStatus } from "../types";
import { downloadFile, proposalFileName } from "./download";

interface DownloadRequest {
  name: string;
  status: ProposalStatus;
  // The stored file that went out, or came back signed.
  fileId?: string;
  // Stands in only when there is no stored file to give.
  fallbackUrl?: string;
}

// Saves a proposal's PDF under its name and status; a failure is reported,
// never thrown.
export const useProposalDownload = () => {
  const { t } = useTranslation();
  const api = useProposalsApi();
  const [downloading, setDownloading] = useState(false);

  const download = async ({
    name,
    status,
    fileId,
    fallbackUrl
  }: DownloadRequest) => {
    setDownloading(true);
    let fileUrl: string | undefined;
    try {
      fileUrl = fileId ? await api.attachmentUrl(fileId) : undefined;
      const url = fileUrl ?? fallbackUrl;
      if (!url) throw new Error("Nothing to download");
      await downloadFile(
        url,
        proposalFileName(name, t(`contracts.proposals.status.${status}`))
      );
    } catch {
      toast.error(t("contracts.proposals.editor.downloadError"));
    } finally {
      if (fileUrl) URL.revokeObjectURL(fileUrl);
      setDownloading(false);
    }
  };

  return { downloading, download };
};
